/**
 * ModulesContainer
 *
 * 책임:
 * - useModules 훅으로 모듈 목록 + 액션 관리
 * - "지금 수집" 버튼 → 확인 모달 → ingest() → 토스트 + 2초 후 Job 상세 이동
 * - enabled 토글 / 수집기 변경 → updateModule() → 토스트 + 목록 새로고침
 * - 로딩/에러 상태 표시
 * - ModulesScreen에 data + 콜백 전달
 *
 * AdminApp에서 screen === 'modules' 일 때 렌더링
 */

import { useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useModules } from '../hooks/useModules';
import { ToastContext } from '../context/ToastContext';
import { ModulesScreen } from '../screens/Modules/ModulesScreen';
import { LoadingView } from '../../design-system/molecules/LoadingView/LoadingView';
import { useMinimumLoading } from '../hooks/useMinimumLoading';
import { ConfirmDialog } from '../../design-system/molecules/ConfirmDialog/ConfirmDialog';

/**
 * 모듈명(module.name) → ingest 경로 키. 이 맵에 있는 모듈만 "지금 수집" 활성화.
 * 대부분 모듈명과 키가 같지만 일부는 다름(td_mkt_stck_dcby_inf→stckdcby, tb_kid_mn_etf_pban→etfpban).
 * 백엔드 OpenAPI(/v3/api-docs/data)의 POST /api/{key}/ingest 기준.
 * {module_name/db_name} : {api_module_name}
 */
const INGEST_KEY: Record<string, string> = {
  etfpdf: 'etfpdf',
  naverdiscuss: 'naverdiscuss',
  reddit: 'reddit',
  seibro: 'seibro',
  tb_kid_mn_etf_pban: 'etfpban',
  // td_mkt_stck_dcby_inf: 'stckdcby',  // 코스모스 x
  tb_ksc_stck_ivstr_inf: "stckivstr",
  tb_krx_new_pban: 'krxnewpban',
  tb_etf_cmpf_inf: 'etfcmpf',
  gpcl_cm_cd_val: 'gpclcmcdval',
  comp_earnings: 'compearn',
};
/** 수집 시작 후 상세로 이동하기 전 대기 시간(ms) — 백엔드가 Job을 등록할 여유. */
const RUN_TO_DETAIL_DELAY = 2000;

/**
 * collectorType별 ingest 바디를 만든다.
 * brightdata 수집기는 로컬 스크랩을 끄고(local_scrap=false) 원격(brightdata)으로 수집해야 함.
 * 그 외 수집기는 바디 없이(기본 input) 호출.
 */
function buildIngestBody(collectorType?: string): object | undefined {
  if (collectorType === 'brightdata') return { local_scrap: false };
  return undefined;
}

export interface ModulesContainerProps {
  /** Job 상세 화면으로 이동 (AdminApp의 openJob). */
  onOpenJob: (jobId: number) => void;
  /** 진입 시 상단으로 스크롤할 모듈명 (편성표 모듈 클릭). null이면 스크롤 안 함. */
  focusModule?: string | null;
  /** 스크롤 처리 완료 알림 → AdminApp이 focusModule 초기화. */
  onFocusHandled?: () => void;
}

export function ModulesContainer({ onOpenJob, focusModule, onFocusHandled }: ModulesContainerProps) {
  const { token } = useAuth();
  // useModules: 모듈 목록 + ingest/updateModule 액션
  const { modules, loading, error, ingest, updateModule } = useModules(token);
  const toast = useContext(ToastContext);

  // 상세 이동 예약 타이머 — 화면을 벗어나면(언마운트) 취소해 강제 이동 방지.
  const navTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (navTimer.current) clearTimeout(navTimer.current); }, []);

  // 확인 모달 대상 모듈명 (null = 닫힘).
  const [confirmModule, setConfirmModule] = useState<string | null>(null);

  // "지금 수집" 버튼 클릭: 바로 실행하지 않고 확인 모달을 연다 (INGEST_KEY 매핑 모듈만).
  const handleRunNow = (name: string) => {
    if (!INGEST_KEY[name]) return;
    setConfirmModule(name);
  };

  // 확인 시 실제 수집 실행:
  // POST /{key}/ingest(바디 없음 → 수집기 기본 input) → 생성된 Job → 2초 후 수집 이력 상세로 이동.
  const handleConfirmRunNow = async () => {
    const name = confirmModule;
    setConfirmModule(null);
    if (!name) return;
    const key = INGEST_KEY[name];
    if (!key) return;
    // brightdata 수집기면 local_scrap=false 바디를 실어 원격 수집을 강제한다.
    const collectorType = modules?.modules.find((m) => m.name === name)?.collectorType;
    const body = buildIngestBody(collectorType);
    try {
      const jobId = await ingest(key, body);
      toast?.show('success', `수집 시작됨 (Job #${jobId}) · 잠시 후 상세로 이동합니다`);
      navTimer.current = setTimeout(() => onOpenJob(jobId), RUN_TO_DETAIL_DELAY);
    } catch (err) {
      const message = err instanceof Error ? err.message : '수집 실행 실패';
      toast?.show('error', message);
    }
  };

  // 확인 모달에 표시할 모듈 이름 (있으면 displayName, 없으면 name).
  const confirmModuleLabel =
    modules?.modules.find((m) => m.name === confirmModule)?.displayName ?? confirmModule;

  // enabled 토글: updateModule로 enabled 필드만 부분 업데이트
  const handleToggleEnabled = async (name: string, enabled: boolean) => {
    try {
      await updateModule(name, { enabled });
      toast?.show('success', `${name} ${enabled ? '활성화' : '비활성화'}됨`);
    } catch (err) {
      const message = err instanceof Error ? err.message : '설정 변경 실패';
      toast?.show('error', message);
    }
  };

  // 수집기 변경: updateModule로 collectorType 필드만 부분 업데이트
  const handleChangeCollector = async (name: string, collectorType: string) => {
    try {
      await updateModule(name, { collectorType });
      toast?.show('success', `${name} 수집기 → ${collectorType}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : '수집기 변경 실패';
      toast?.show('error', message);
    }
  };

  // 수정 버튼: 수정 모달은 아직 미구현 → 안내 토스트
  const handleEdit = (name: string) => {
    toast?.show('info', `준비 중..(TBD)`);
  };

  // 로딩 상태 — 실제 로딩이 0.7초보다 빨리 끝나도 최소 0.7초는 로딩 화면을 유지.
  const showLoading = useMinimumLoading(loading);
  if (showLoading) {
    return <LoadingView message="모듈 목록 로딩 중…" />;
  }

  // 에러 상태
  if (error) {
    return (
      <div
        style={{
          padding: '2rem',
          color: '#c00',
          backgroundColor: '#fee',
          borderRadius: '0.5rem',
          margin: '2rem',
        }}
      >
        <div>❌ {error}</div>
      </div>
    );
  }

  // ModulesScreen 렌더링: 화면이 기대하는 data={{modules}} 형태로 전달
  // ModuleDto[]는 ModuleConfig[]에 구조적으로 할당 가능 (displayName만 추가 필드)
  return (
    <>
      <ModulesScreen
        data={{ modules: modules?.modules ?? [] }}
        focusModule={focusModule}
        onFocusHandled={onFocusHandled}
        onRunNow={handleRunNow}
        isRunNowEnabled={(name) => name in INGEST_KEY}
        onEdit={handleEdit}
        onToggleEnabled={handleToggleEnabled}
        onChangeCollector={handleChangeCollector}
      />
      <ConfirmDialog
        open={confirmModule !== null}
        title="지금 수집"
        message={`${confirmModuleLabel} 모듈을 지금 수집할까요? 완료 후 생성된 Job 상세로 이동합니다.`}
        confirmLabel="수집 시작"
        cancelLabel="취소"
        onConfirm={handleConfirmRunNow}
        onCancel={() => setConfirmModule(null)}
      />
    </>
  );
}
